import { useEffect, useRef, useState } from 'react'
import {
  App,
  Checkbox,
  ConfigProvider,
  Drawer,
  Flex,
  Input,
  Spin,
  Table,
  Tooltip,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import SearchIcon from '@/assets/search.svg?react'
import BrandButton from '@/components/BrandButton'
import { PageTabs } from '@/components/Tabs'
import { DrawerCloseExtra } from '@/pages/app-detail/versions/drawer/parts'
import {
  SCOPE_PAGE_SIZE,
  formatRelatedApi,
  listAllScopeCatalog,
  listScopeCatalog,
  type ScopeRef,
} from '@/api/scopes'
import {
  appScopeId,
  parseAppScopeId,
  type ScopeCatalogItem,
  type ScopeIdentity,
} from '@/types'
import { filterSelectionState, omitScopeIds } from './filterSelection'
import ScopePager from './ScopePager'
import styles from './RequestAccessDrawer.module.scss'

type Props = {
  open: boolean
  appId: string
  onClose: () => void
  onSubmit: (scopes: ScopeRef[]) => Promise<void>
}

const SEAMLESS = {
  activeShadow: 'none',
  activeBorderColor: 'transparent',
  hoverBorderColor: 'transparent',
}

const drawerBg = 'rgba(255, 255, 255, 0.85)'

function catalogRowKey(item: ScopeCatalogItem) {
  return appScopeId(item.scope_id, item.identity_type)
}

export default function RequestAccessDrawer({
  open,
  appId,
  onClose,
  onSubmit,
}: Props) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [identity, setIdentity] = useState<ScopeIdentity>('app')
  const [category, setCategory] = useState<string | 'all'>('all')
  const [categories, setCategories] = useState<string[]>([])
  const [categoryNames, setCategoryNames] = useState<Record<string, string>>({})
  const [keyword, setKeyword] = useState('')
  const [debouncedKeyword, setDebouncedKeyword] = useState('')
  const [page, setPage] = useState(1)
  const [list, setList] = useState<ScopeCatalogItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState<Record<ScopeIdentity, string[]>>({
    app: [],
    user: [],
  })
  const [submitting, setSubmitting] = useState(false)
  const [selectingAll, setSelectingAll] = useState(false)
  const [known, setKnown] = useState<{ key: string; ids: string[] } | null>(null)
  const appliedKeyword = useRef('')
  const scanRef = useRef(0)
  const tableWrapRef = useRef<HTMLDivElement>(null)
  const [tableBodyY, setTableBodyY] = useState(480)

  useEffect(() => {
    if (!open) return
    let observer: ResizeObserver | undefined
    let timer = 0
    const bind = () => {
      const el = tableWrapRef.current
      if (!el) return false
      const measure = () => {
        setTableBodyY(Math.max(160, el.clientHeight - 48))
      }
      measure()
      observer = new ResizeObserver(measure)
      observer.observe(el)
      return true
    }
    if (!bind()) timer = window.setTimeout(bind, 50)
    return () => {
      window.clearTimeout(timer)
      observer?.disconnect()
    }
  }, [open])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = keyword.trim()
      if (appliedKeyword.current === next) return
      appliedKeyword.current = next
      scanRef.current += 1
      setSelectingAll(false)
      setKnown(null)
      setDebouncedKeyword(next)
      setPage(1)
    }, 300)
    return () => window.clearTimeout(timer)
  }, [keyword])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    listScopeCatalog({
      page,
      page_size: SCOPE_PAGE_SIZE,
      keyword: debouncedKeyword || undefined,
      category: category === 'all' ? undefined : category,
      identity_type: identity,
      app_id: appId,
    })
      .then((result) => {
        if (cancelled) return
        const lastPage = Math.max(1, Math.ceil(result.total / SCOPE_PAGE_SIZE))
        if (page > lastPage) {
          setPage(lastPage)
          return
        }
        setList(result.list)
        setTotal(result.total)
        setCategories(result.categories)
        setCategoryNames(result.categoryNames)
        const grantedIds = new Set(
          result.list.filter((row) => row.granted).map(catalogRowKey),
        )
        if (grantedIds.size > 0) {
          setSelected((prev) => omitScopeIds(prev, grantedIds))
          setKnown((prev) => {
            if (!prev) return prev
            const ids = prev.ids.filter((id) => !grantedIds.has(id))
            return ids.length === prev.ids.length ? prev : { ...prev, ids }
          })
        }
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setList([])
        setTotal(0)
        setCategories([])
        setCategoryNames({})
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, appId, identity, category, debouncedKeyword, page])

  const reset = () => {
    appliedKeyword.current = ''
    scanRef.current += 1
    setIdentity('app')
    setCategory('all')
    setKeyword('')
    setDebouncedKeyword('')
    setPage(1)
    setSelected({ app: [], user: [] })
    setKnown(null)
    setSelectingAll(false)
    setSubmitting(false)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const selectedForTab = selected[identity]
  const totalSelected = selected.app.length + selected.user.length
  const selectableKeys = list.filter((row) => !row.granted).map(catalogRowKey)
  const filterKey = `${appId}\0${identity}\0${category}\0${debouncedKeyword}`
  const knownIds = known?.key === filterKey ? known.ids : null
  const header = filterSelectionState({
    total,
    pageSize: SCOPE_PAGE_SIZE,
    pageIds: selectableKeys,
    selectedIds: new Set(selectedForTab),
    knownIds,
  })
  const selectAllDisabled =
    selectingAll ||
    total === 0 ||
    (total <= SCOPE_PAGE_SIZE && selectableKeys.length === 0) ||
    (knownIds !== null && knownIds.length === 0)

  const toggleRow = (key: string, checked: boolean, alreadyOpen: boolean) => {
    if (alreadyOpen || selectingAll) return
    setSelected((prev) => {
      const cur = prev[identity]
      const next = checked ? [...cur, key] : cur.filter((k) => k !== key)
      return { ...prev, [identity]: next }
    })
  }

  const selectAll = async () => {
    const ticket = ++scanRef.current
    const key = filterKey
    const tab = identity
    setSelectingAll(true)
    try {
      const rows = await listAllScopeCatalog({
        keyword: debouncedKeyword || undefined,
        category: category === 'all' ? undefined : category,
        identity_type: tab,
        app_id: appId,
      })
      if (ticket !== scanRef.current) return
      const grantedIds = new Set(rows.filter((row) => row.granted).map(catalogRowKey))
      const ids = rows.filter((row) => !row.granted).map(catalogRowKey)
      setKnown({ key, ids })
      setSelected((prev) => {
        const cur = new Set(prev[tab].filter((id) => !grantedIds.has(id)))
        ids.forEach((id) => cur.add(id))
        return { ...prev, [tab]: [...cur] }
      })
    } catch {
      if (ticket !== scanRef.current) return
      message.error(t('scope.loadFailed'))
    } finally {
      if (ticket === scanRef.current) setSelectingAll(false)
    }
  }

  const toggleAll = (checked: boolean) => {
    if (checked) {
      void selectAll()
      return
    }
    const drop = new Set(knownIds ?? selectableKeys)
    setSelected((prev) => ({
      ...prev,
      [identity]: prev[identity].filter((id) => !drop.has(id)),
    }))
  }

  const columns: ColumnsType<ScopeCatalogItem> = [
      {
        title: (
          <Flex align="center" gap={12} style={{ whiteSpace: 'nowrap' }}>
            <Checkbox
              checked={header.all}
              indeterminate={header.some}
              disabled={selectAllDisabled}
              onChange={(e) => toggleAll(e.target.checked)}
            />
            <span>{t('scope.name')}</span>
          </Flex>
        ),
        dataIndex: 'name',
        render: (name: string, record) => {
          const key = catalogRowKey(record)
          const alreadyOpen = record.granted
          const checked = alreadyOpen || selectedForTab.includes(key)
          return (
            <Flex align="center" gap={12}>
              <Tooltip title={alreadyOpen ? t('scope.alreadyOpen') : undefined}>
                <span style={{ display: 'inline-flex' }}>
                  <Checkbox
                    checked={checked}
                    disabled={alreadyOpen || selectingAll}
                    onChange={(e) => toggleRow(key, e.target.checked, alreadyOpen)}
                  />
                </span>
              </Tooltip>
              <div style={{ minWidth: 0 }}>
                <Flex align="center" gap={8}>
                  <span style={{ fontSize: 13, lineHeight: '20px', color: '#000' }}>
                    {name}
                  </span>
                  {alreadyOpen && (
                    <span className={styles.granted}>{t('scope.alreadyOpenTag')}</span>
                  )}
                </Flex>
                <div
                  style={{
                    fontSize: 13,
                    lineHeight: '20px',
                    color: 'rgba(0, 0, 0, 0.4)',
                  }}
                >
                  {record.scope_id}
                </div>
              </div>
            </Flex>
          )
        },
      },
      {
        title: t('scope.relatedApisColumn'),
        width: 280,
        render: (_, record) =>
          record.related_apis.length === 0 ? null : (
            <Flex vertical gap={2}>
              {record.related_apis.map((api, index) => (
                <span key={`${formatRelatedApi(api)}-${index}`} className={styles.apiLink}>
                  {formatRelatedApi(api)}
                </span>
              ))}
            </Flex>
          ),
      },
    ]

  const submit = async () => {
    if (submitting) return
    const grantedKeys = new Set(list.filter((row) => row.granted).map(catalogRowKey))
    const scopes = [...selected.app, ...selected.user].flatMap((id) => {
      if (grantedKeys.has(id)) return []
      const parsed = parseAppScopeId(id)
      return parsed ? [parsed] : []
    })
    if (scopes.length === 0) return
    setSubmitting(true)
    try {
      await onSubmit(scopes)
      reset()
    } catch {
      setSubmitting(false)
    }
  }

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      size={925}
      closable={false}
      destroyOnHidden
      title={
        <span style={{ fontSize: 18, fontWeight: 500, lineHeight: '25px', color: '#000' }}>
          {t('scope.requestTitle')}
        </span>
      }
      extra={<DrawerCloseExtra onClose={handleClose} />}
      styles={{
        body: {
          padding: '10px 20px 20px',
          background: drawerBg,
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          overflow: 'hidden',
        },
        wrapper: { width: 925 },
        section: {
          background: drawerBg,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid #dcdcdc',
          borderRadius: 0,
          boxShadow: '0px 16px 22px -6px rgba(28, 28, 28, 0.16)',
        },
        header: {
          background: drawerBg,
          borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
          padding: 20,
        },
        footer: {
          background: drawerBg,
          borderTop: '1px solid #dcdcdc',
          boxShadow: '0px -4px 20px 0px rgba(0, 0, 0, 0.06)',
          padding: '0 16px',
          height: 60,
        },
      }}
      footer={
        <Flex align="center" justify="space-between" style={{ height: '100%' }}>
          <Flex align="center" gap={10}>
            <span style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 13, lineHeight: '20px' }}>
              {t('scope.selectedLabel')}
            </span>
            <Flex align="center" gap={16}>
              <span style={{ color: '#000', fontSize: 13, lineHeight: '20px' }}>
                {t('scope.identityTab_app')} ({selected.app.length})
              </span>
              <span style={{ color: '#000', fontSize: 13, lineHeight: '20px' }}>
                {t('scope.identityTab_user')} ({selected.user.length})
              </span>
            </Flex>
          </Flex>
          <Flex align="center" gap={20}>
            <BrandButton
              variant="outline"
              style={{ padding: '0 18px' }}
              onClick={() => setSelected({ app: [], user: [] })}
            >
              {t('scope.cancelSelection')}
            </BrandButton>
            <BrandButton
              disabled={totalSelected === 0 || submitting}
              style={{ padding: '0 18px' }}
              onClick={() => void submit()}
            >
              {t('scope.requestAccess')}
            </BrandButton>
          </Flex>
        </Flex>
      }
    >
      <ConfigProvider theme={{ components: { Input: SEAMLESS } }}>
        <div className={styles.searchBar}>
          <Input
            variant="borderless"
            allowClear
            className={styles.searchInput}
            suffix={<SearchIcon width={16} height={16} />}
            placeholder={t('scope.searchPlaceholder')}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </div>
      </ConfigProvider>

      <div>
        <div className={styles.tabs}>
          <PageTabs
            activeKey={identity}
            onChange={(key) => {
              const next = key as ScopeIdentity
              if (next === identity) return
              scanRef.current += 1
              setSelectingAll(false)
              setKnown(null)
              setIdentity(next)
              setCategory('all')
              setPage(1)
              setCategories([])
            }}
            items={[
              { key: 'app', label: t('scope.identityTab_app') },
              { key: 'user', label: t('scope.identityTab_user') },
            ]}
          />
        </div>
        <div className={styles.tabLine} />
      </div>

      <Flex gap={20} style={{ flex: 1, minHeight: 0 }}>
        <div className={styles.sider}>
          <button
            type="button"
            className={`${styles.category} ${category === 'all' ? styles.categoryActive : ''}`}
            onClick={() => {
              scanRef.current += 1
              setSelectingAll(false)
              setKnown(null)
              setCategory('all')
              setPage(1)
            }}
          >
            {t('scope.categoryAll')}
          </button>
          {categories.map((item) => (
            <button
              key={item}
              type="button"
              className={`${styles.category} ${category === item ? styles.categoryActive : ''}`}
              onClick={() => {
                scanRef.current += 1
                setSelectingAll(false)
                setKnown(null)
                setCategory(item)
                setPage(1)
              }}
            >
              {categoryNames[item] || item}
            </button>
          ))}
        </div>
        <Flex vertical style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
          <div className={styles.tableWrap} ref={tableWrapRef}>
            <Table
              rowKey={catalogRowKey}
              columns={columns}
              dataSource={list}
              loading={loading}
              pagination={false}
              tableLayout="fixed"
              scroll={{ y: tableBodyY }}
              locale={{
                emptyText: loading ? <Spin /> : t('scope.catalogEmpty'),
              }}
            />
          </div>
          <ScopePager
            page={page}
            pageSize={SCOPE_PAGE_SIZE}
            total={total}
            onChange={setPage}
          />
        </Flex>
      </Flex>
    </Drawer>
  )
}
